import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col } from 'react-flexbox-grid';

import { MediaViewer, Loader, Button } from 'shared/components';
import requestsStatuses from 'modules/common/constants/requestsStatuses';

import MediaGalleryTile from './elements/MediaGalleryTile';
import mediaType from './mediaType';
import mediaStatus from '../../constants/mediaStatus';
import theme from './MediaGallery.scss';

const download = require('downloadjs');

const propTypes = {
  getUploadInstructions: PropTypes.func.isRequired,
  uploadInstructionsStatus: PropTypes.string.isRequired,
  uploadInstructions: PropTypes.string.isRequired,
  retryUploadMedia: PropTypes.func.isRequired,
  getCurrentConsultationMedia: PropTypes.func.isRequired,
  startObserveFileMetaDataUpdates: PropTypes.func.isRequired,
  stopObserveFileMetaDataUpdates: PropTypes.func.isRequired,
  fetchMediaData: PropTypes.func.isRequired,
  deleteConsultationMediaWithConfirm: PropTypes.func,
  editFileMetadataParametersWithConfirm: PropTypes.func,
  allowedActions: PropTypes.array.isRequired,
  status: PropTypes.string.isRequired,
  appointmentId: PropTypes.string.isRequired,
  filesTotal: PropTypes.number.isRequired,
  views: PropTypes.array.isRequired,
  isArchived: PropTypes.bool.isRequired,
  uploads: PropTypes.array,
  intl: PropTypes.object.isRequired,
};

const defaultProps = {
  uploads: [],
  deleteConsultationMediaWithConfirm: noop,
  editFileMetadataParametersWithConfirm: noop,
};

const LoadingStatePlaceholder = () => (
  <div className={ theme.root }>
    <div className={ theme.loader }>
      <Loader />
    </div>
  </div>
);

const FailedStatePlaceholder = injectIntl(({ intl, getCurrentConsultationMedia }) => (
  <div className={ theme.root }>
    <div className={ theme.couldNotLoad }>
      <FormattedMessage
        id="schedule.appointmentsSidebarFetchErrorPlaceholder.fetchError"
        defaultMessage="** Could not load list. Check internet connection and click the refresh button"
      />
      <div>
        <Button
          testId="media-refresh-button"
          buttonType="naked"
          className={ theme.refresh }
          label={ intl.formatMessage({
            id: 'schedule.appointmentsSidebarFetchErrorPlaceholder.refresh',
            defaultMessage: '** Refresh',
          }) }
          iconName="refresh"
          size="large"
          onClick={ getCurrentConsultationMedia }
          mediaResized
          iconPositionLeft
        />
      </div>
    </div>
  </div>
));

class MediaGallery extends PureComponent {

  state = {
    showViewId: null,
  };

  componentDidMount() {
    const {
      startObserveFileMetaDataUpdates,
      getCurrentConsultationMedia,
      getUploadInstructions,
    } = this.props;

    startObserveFileMetaDataUpdates(this.props.appointmentId);
    getUploadInstructions();
    getCurrentConsultationMedia();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.appointmentId !== this.props.appointmentId) {
      this.props.startObserveFileMetaDataUpdates(this.props.appointmentId);
    }
  }

  componentWillUnmount() {
    this.props.stopObserveFileMetaDataUpdates();
  }

  onMediaGalleryTileClick = ({ id, status, mimeType, name, downloadRoute }) => {
    if (status !== mediaStatus.UPLOADED) {
      return;
    }

    if (mimeType === mediaType.PDF) {
      this.props.fetchMediaData(downloadRoute)
        .then((response) => response.blob())
        .then((blob) => download(blob, name));
    } else {
      this.showView(id);
    }
  };

  getViews() {
    return this.props.views
      .filter(view => view.mimeType !== mediaType.PDF && view.status !== mediaStatus.CREATED);
  }

  close = () => {
    this.setState({ showViewId: null });
  };

  showView(showViewId) {
    this.setState({ showViewId });
  }

  render() {
    const { showViewId } = this.state;
    const {
      status,
      uploadInstructionsStatus,
      uploadInstructions,
      filesTotal,
      uploads,
      views,
      getCurrentConsultationMedia,
      deleteConsultationMediaWithConfirm,
      editFileMetadataParametersWithConfirm,
      retryUploadMedia,
      allowedActions,
      fetchMediaData,
      isArchived,
      style,
      intl,
    } = this.props;

    if (status === requestsStatuses.pending || uploadInstructionsStatus === requestsStatuses.pending) {
      return <LoadingStatePlaceholder />;
    }

    if (status === requestsStatuses.failure) {
      return (
        <FailedStatePlaceholder
          getCurrentConsultationMedia={ getCurrentConsultationMedia }
        />
      );
    }

    const images = this.getViews();
    const mediaViewerShown = showViewId && images.some(i => i.id === showViewId);

    return (
      <div className={ theme.root } style={ style }>

        { !filesTotal && (
          <div className={ theme.noMedia }>
            <Choose>
              <When condition={ uploadInstructions && !isArchived }>
                { uploadInstructions }
              </When>
              <Otherwise>
                { intl.formatMessage({ id: 'schedule.patientInfoTab.noMediaUploaded', defaultMessage: '** No media was uploaded.' }) }
              </Otherwise>
            </Choose>
          </div>
        ) }

        { !!filesTotal && (
          <Fragment>
            { mediaViewerShown && (<MediaViewer
              onClose={ this.close }
              onDelete={ deleteConsultationMediaWithConfirm }
              views={ images }
              showViewId={ showViewId }
              allowedActions={ allowedActions }
              fetchMediaData={ fetchMediaData }
              parentId="appointment-info-sidebar"
            />
            ) }

            <Row className={ theme.images }>
              { uploads.map((file) => (
                <Col
                  key={ file.id }
                  xs={ 6 }
                  sm={ 6 }
                  md={ 4 }
                >
                  <MediaGalleryTile
                    id={ file.id }
                    onClick={ file.status === mediaStatus.FAILED ? retryUploadMedia : noop }
                    status={ file.status }
                    comment={ file.comment }
                    mimeType={ file.mimeType }
                  />
                </Col>
              )) }

              { views.map((view) => (
                <Col
                  key={ view.id }
                  xs={ 6 }
                  sm={ 6 }
                  md={ 4 }
                >
                  <MediaGalleryTile
                    id={ view.id }
                    onClick={ this.onMediaGalleryTileClick }
                    onDelete={ deleteConsultationMediaWithConfirm }
                    onEdit={ editFileMetadataParametersWithConfirm }
                    status={ view.status }
                    name={ view.name }
                    comment={ view.comment }
                    thumbnail={ view.thumbnail }
                    downloadRoute={ view.downloadRoute }
                    mimeType={ view.mimeType }
                    canBeRemoved={ view.canBeRemoved }
                    canBeUpdated={ view.canBeUpdated }
                  />
                </Col>
              )) }

            </Row>
          </Fragment>
        ) }

      </div>
    );
  }
}

MediaGallery.propTypes = propTypes;
MediaGallery.defaultProps = defaultProps;

export default MediaGallery;
